import {FieldAutoComplete, FormSection, ModalDialog} from "../../ui"
import {
    createEmptyDocument,
    DialogStore,
    slsCreateStore,
    slsUserStore,
    VdaCustomerItem,
    VdaPlacementItem
} from "../../../store"
import {PreservationMessageDto, PreservationTypeDto, VehicleDataAcquisitionDocumentDto,} from "../../../api/vda"
import {VdaDocumentCor1Form} from "./documents/VdaDocumentCor1Form"
import {VdaDocumentCor2Form} from "./documents/VdaDocumentCor2Form"
import {VdaDocumentDefaultForm} from "./documents/VdaDocumentDefaultForm"
import "./VdaForm.scss"
import {FieldInput, FieldMultiSelect} from "@kroschke/react-ui"
import {convertDocumentType, FieldValidationDto} from "../../../api"
import {PlacementProcessOriginDto, PlacementProcessStatusDto} from "../../../api/sls"
import {useState} from "react"
import {useNavigate} from "react-router"
import {VdaDocumentLicensePlateForm} from "./documents/VdaDocumentLicensePlateForm"
import {VdaDocumentCarKeyForm} from "./documents/VdaDocumentCarKeyForm";

export type VdaDocumentType =
    'COC' |
    'COR_I' |
    'COR_II' |
    'ABE' |
    'CERT' |
    'CONTRACT' |
    'SALARY_STATEMENT' |
    'ID_CARD' |
    'OTHER' |
    'CAR_KEY' |
    'LICENSE_PLATE_FRONT' |
    'LICENSE_PLATE_REAR' |
    'WARRANTY' |
    'RADIO_CODE'

type DropDownOption = {
    label: string
    value: VdaDocumentType
}

export const DocumentTypeLabels: Record<VdaDocumentType, string> = {
    'COC': convertDocumentType('COC'),
    'COR_I': convertDocumentType('COR_I'),
    'COR_II': convertDocumentType('COR_II'),
    'ABE': convertDocumentType('ABE'),
    'CERT': convertDocumentType('CERT'),
    'CONTRACT': convertDocumentType('CONTRACT'),
    'SALARY_STATEMENT': convertDocumentType('SALARY_STATEMENT'),
    'ID_CARD': convertDocumentType('ID_CARD'),
    'OTHER': convertDocumentType('OTHER'),
    'CAR_KEY': convertDocumentType('CAR_KEY'),
    'LICENSE_PLATE_FRONT': convertDocumentType('LICENSE_PLATE_FRONT'),
    'LICENSE_PLATE_REAR': convertDocumentType('LICENSE_PLATE_REAR'),
    'WARRANTY': convertDocumentType('WARRANTY'),
    'RADIO_CODE': convertDocumentType('RADIO_CODE')
} as const

const DocumentDropDownOptions: DropDownOption[] = Object.entries(DocumentTypeLabels)
    .map(([value, label]) => ({
        value: value as VdaDocumentType,
        label
    }))

export interface VdaFormData {
    customer: VdaCustomerItem | undefined
    placement: VdaPlacementItem | undefined
    vin: string
    documents: VehicleDataAcquisitionDocumentDto[]
    licensePlate: string
    vinCheckDigit: string | undefined
}

export interface VdaFormValidation {
    customer?: string
    placement?: string
    vin?: string
    vinCheckDigit?: string
    documents?: string
    errorByDocument?: FieldValidationDto[]
}

export type LeadingLicensePlateType = 'COR_I' | 'COR_II' | 'LICENSE_PLATE_FRONT' | 'LICENSE_PLATE_REAR'

interface VdaFormDataProps {
    messages?: PreservationMessageDto[],
    validation: VdaFormValidation,
    disabled?: boolean
    value: VdaFormData
    customers: VdaCustomerItem[]
    placements: VdaPlacementItem[]
    onChange: (value: VdaFormData) => any
    onBlur: () => any
    onSearchCustomer: (query: string) => Promise<VdaCustomerItem[]>
    leadingLicensePlate?: LeadingLicensePlateType
}

function getNotificationType(code: string) {
    if (code.startsWith('E')) {
        return 'is-danger'
    }
    return 'is-info'
}

function translateErrorCode(code: string): string {
    switch (code) {
        case 'E0000':
            return 'Fehler'
        case 'E0001':
            return 'Unerwarteter Fehler beim Speichervorgang'
        case 'E0003':
            return 'Dokumenten-Id noch aktiv'
        case 'E0004':
            return 'ZB1 Nummer noch aktiv'
        case 'E0005':
            return 'ZB2 Nummer noch aktiv'
        case 'E0006':
            return 'FIN noch aktiv für Dokument'
        case 'E0007':
            return 'Ein Dokumententyp wurde mehrmals verwendet'
        case 'E0008':
            return 'Dokumenten-Id darf nicht doppelt vorkommen'
    }
    return code
}

function MessagesBlock({messages}: { messages: PreservationMessageDto[] }) {
    return <>
        {Boolean(messages.length) && <div style={{
            marginTop: '-1em',
            marginLeft: '-1em',
            marginRight: '-1em',
            marginBottom: '1em'
        }}>
            {messages.map((e, index) => {
                return <article key={index}
                                style={{borderRadius: '0'}}
                                className={`message ${getNotificationType(e.code || '')}`}>
                    <div className="message-body"
                         style={{borderRadius: '0'}}>{`${translateErrorCode(e.code || '')}${e.documentId ? `: ${e.documentId}` : ''}`}
                    </div>
                </article>
            })}
        </div>}
    </>
}

function PlacementProcessCreateButton({
                                          disabled,
                                          customer,
                                          placement,
                                          onChange
                                      }: {
    disabled?: boolean
    customer: VdaCustomerItem | undefined
    placement: VdaPlacementItem | undefined
    onChange: (value: VdaPlacementItem) => any
}) {
    const [creatingSls, setCreatingSls] = useState<boolean>(false)
    return <button
        disabled={disabled || !customer || !!placement}
        className={`button is-light ${creatingSls ? 'is-loading' : ''}`}
        style={{marginTop: '0.25em'}}
        onClick={async () => {
            setCreatingSls(true)
            await slsUserStore.init()
            const correlationId = `${customer?.customerNumber}-${new Date().toISOString().replace(/\D/g, '')}`
            slsCreateStore.formData = {
                correlationId,
                origin: PlacementProcessOriginDto.VDA,
                user: await slsUserStore.getActiveUser(),
                customer: customer,
                status: PlacementProcessStatusDto.ACTIVE,
                documents: undefined
            }
            const slsId = await slsCreateStore.create()
            if (slsId) {
                onChange({
                    id: slsId!,
                    label: correlationId
                })
                onChange({
                    id: slsId!,
                    label: correlationId
                })
            }
            setCreatingSls(false)
        }}
    >Einlagerungsprozess anlegen</button>
}

function PlacementProcessDetailButton({
                                          placement,
                                      }: {
    placement: VdaPlacementItem
}) {
    const navigate = useNavigate()
    return <button
        title={`Einlagerungsprozess`}
        className={`button is-primary`}
        style={{marginTop: '0.25em'}}
        onClick={async () => {
            navigate(`/sls/${placement?.id}`)

        }}
    ><span className="material-symbols-outlined">expand_circle_right</span></button>
}

function getUniqueDocumentTypes(documents: VehicleDataAcquisitionDocumentDto[]): string[]{
    const set = new Set<string>()
    documents.forEach(d => set.add(d.type))
    return Array.from(set.values())
}

function DeleteVdaDocHeader({
                                onDelete
                            }: {
    onDelete: () => any
}) {

    return <>
        <button className={"button is-small is-ghost has-text-danger"}>
            <span className="material-symbols-outlined"
                  onClick={() => onDelete()}>delete</span>
        </button>
    </>
}

function VdaForm({
                     value,
                     disabled,
                     onChange,
                     onBlur,
                     placements,
                     customers,
                     validation,
                     onSearchCustomer,
                     messages,
                     leadingLicensePlate,
                 }: VdaFormDataProps) {
    const messageData = messages || []

    const cocHasError = messageData.filter(e => e.type === PreservationTypeDto.COC && e.code?.startsWith('E')).length > 0
    const zb1HasError = messageData.filter(e => e.type === PreservationTypeDto.ZB1 && e.code?.startsWith('E')).length > 0
    const zb2HasError = messageData.filter(e => e.type === PreservationTypeDto.ZB2 && e.code?.startsWith('E')).length > 0

    const [deleteDocumentId, setDeleteDocumentId] = useState<string | undefined>(undefined)
    const [deleteDialog] = useState(new DialogStore())

    const createDefaultDocumentForm = (
        label: string,
        document: VehicleDataAcquisitionDocumentDto,
        index: number,
        errorByDocument: FieldValidationDto[]
    ) => {
        return (
            <VdaDocumentDefaultForm
                label={label}
                disabled={disabled}
                key={index}
                validation={{
                    documentId: errorByDocument?.filter(error => error.field?.endsWith('.documentId'))[0]?.message,
                    position: errorByDocument?.filter(error => error.field?.endsWith('.position'))[0]?.message
                }}
                value={{
                    vin: value.vin,
                    position: document.position,
                    documentId: document.documentId
                }}
                onChange={(formData) => {
                    const updatedList = value.documents.map(
                        temp => temp === document ? {
                            ...temp,
                            ...formData
                        } : temp
                    )
                    onChange({
                        ...value,
                        documents: updatedList
                    })
                }}
                onBlur={onBlur}
                header={!disabled && <DeleteVdaDocHeader onDelete={()=>{
                    setDeleteDocumentId(document.documentId)
                    deleteDialog.dialogOpen = true
                }}/> }

            />
        )
    }
    return <div
        className={`cmp-vda-form ${cocHasError && 'has-error-coc'} ${zb1HasError && 'has-error-zb1'} ${zb2HasError && 'has-error-zb2'}`}>
        <ModalDialog
            store={deleteDialog}
            actions={[
                {
                    label: "Abbrechen",
                    execute: async function () {
                        deleteDialog.dialogOpen = false
                        setDeleteDocumentId(undefined)
                        onBlur()
                    },
                    className: ' is-rounded is-small'
                },
                {
                    label: "Entfernen",
                    execute: async function () {
                        const docs = value.documents.filter(doc => doc.documentId !== deleteDocumentId)
                        onChange({
                            ...value,
                            documents: docs
                        })
                        onBlur()
                        setDeleteDocumentId(undefined)
                        deleteDialog.dialogOpen = false
                    },
                    className: 'is-rounded is-small is-danger'
                }
            ]}
            title={"Dokument entfernen?"}
        >
            Sind Sie sich sicher, dass Sie das Dokument entfernen möchten?
        </ModalDialog>
        <MessagesBlock messages={messageData}/>
        <FormSection
            label={"Allgemein"}
            children={
                <>
                    <div className={"columns"}>
                        <div className={"column is-one-third"}>
                            <FieldAutoComplete
                                label={"Kunde"}
                                disabled={disabled}
                                required={true}
                                help={validation.customer}
                                invalid={Boolean(validation.customer)}
                                value={value.customer}
                                options={customers}
                                itemRenderer={c => c.label}
                                search={onSearchCustomer}
                                onBlur={() => !disabled && onBlur()}
                                onChange={(customer: VdaCustomerItem | undefined) => {
                                    onChange({
                                        ...value,
                                        customer: customer!!
                                    })
                                    onBlur()
                                }}
                            />
                        </div>
                        <div className={"column is-one-third"}>
                            <FieldAutoComplete
                                label={"Einlagerungsprozess"}
                                disabled={disabled || !value.customer}
                                required={true}
                                help={validation.placement}
                                invalid={Boolean(validation.placement)}
                                value={value.placement}
                                options={placements}
                                itemRenderer={c => c.label}
                                onBlur={() => !disabled && onBlur()}
                                onChange={(placement: VdaPlacementItem | undefined) => {
                                    onChange({
                                        ...value,
                                        placement: placement!,
                                    })
                                    onBlur()
                                }}
                            />
                        </div>
                        <div className={"column is-one-third"}>
                            <>
                                {!value.placement && <PlacementProcessCreateButton
                                    placement={value.placement}
                                    customer={value.customer}
                                    disabled={disabled}
                                    onChange={placement => {
                                        onChange({
                                            ...value,
                                            placement
                                        })
                                        onBlur()
                                    }}
                                />}
                                {value.placement?.id && <>
                                    <PlacementProcessDetailButton placement={value.placement}/>
                                </>}
                            </>
                        </div>
                    </div>
                    <div className={"columns"}>
                        <div className={"column is-one-third"}>
                            <FieldInput
                                disabled={disabled}
                                required={true}
                                maxLength={17}
                                help={validation.vin}
                                invalid={Boolean(validation.vin)}
                                value={value.vin}
                                onBlur={() => !disabled && onBlur()}
                                onChange={fin => onChange({
                                    ...value,
                                    vin: fin
                                })}
                                inputTransform={(v: string) => {
                                    return v.toUpperCase()
                                }}
                                label={"Fahrgestellnummer"}
                            />
                        </div>
                        <div className={"column is-one-third"}>
                            <FieldMultiSelect
                                label={"Dokumente"}
                                options={DocumentDropDownOptions}
                                disabled={disabled}
                                required={true}
                                value={getUniqueDocumentTypes(value.documents)}
                                help={validation.documents}
                                invalid={Boolean(validation.documents)}
                                onBlur={() => onBlur()}
                                onChange={selections => {

                                    const validSelection = selections.map(value => value as VdaDocumentType)

                                    if (value.documents.length > selections.length) {
                                        onChange({
                                            ...value,
                                            documents: value.documents.filter(doc => validSelection.includes(doc.type))
                                        })
                                    } else {
                                        const missingTypes = validSelection.filter(
                                            type => !(value.documents.map(doc => doc.type.valueOf()).includes(type))
                                        )

                                        const updatedList = [
                                            ...value.documents,
                                            ...missingTypes.map(type => createEmptyDocument(type))
                                        ]

                                        onChange({
                                            ...value,
                                            documents: updatedList
                                        })
                                    }
                                }}
                            />
                        </div>
                    </div>
                </>
            }
        />
        {value.documents.map((document, index) => {

            const errorByDocument = validation.errorByDocument?.filter(error => error.field?.startsWith(`documents[${index}]`)) || []

            switch (document.type) {
                case "COC":
                    return createDefaultDocumentForm(DocumentTypeLabels.COC, document, index, errorByDocument)
                case "COR_I":
                    return <VdaDocumentCor1Form
                        label={DocumentTypeLabels.COR_I}
                        disabled={disabled}
                        disableLicensePlate={leadingLicensePlate !== document.type}
                        key={index}
                        validation={{
                            vinCheckDigit: validation.vinCheckDigit,
                            documentId: errorByDocument?.filter(error => error.field?.endsWith('.documentId'))[0]?.message,
                            position: errorByDocument?.filter(error => error.field?.endsWith('.position'))[0]?.message,
                            corSerial: errorByDocument?.filter(error => error.field?.endsWith('.corSerial'))[0]?.message,
                            licensePlate: errorByDocument?.filter(error => error.field?.endsWith('.licensePlate'))[0]?.message,
                            registrationDateFirst: errorByDocument?.filter(error => error.field?.endsWith('.registrationDateFirst'))[0]?.message,
                            registrationDateLatest: errorByDocument?.filter(error => error.field?.endsWith('.registrationDateLatest'))[0]?.message,
                            deregistrationDate: errorByDocument?.filter(error => error.field?.endsWith('.deregistrationDate'))[0]?.message,
                        }}
                        value={{
                            vin: value.vin,
                            vinCheckDigit: value.vinCheckDigit,
                            documentId: document.documentId,
                            position: document.position,
                            corSerial: document.corSerial,
                            licensePlate: value.licensePlate,
                            registrationDateFirst: document.registrationDateFirst,
                            registrationDateLatest: document.registrationDateLatest,
                            deregistrationDate: document.deregistrationDate
                        }}

                        onChange={(formData) => {
                            console.log(formData)
                            const updatedList: VehicleDataAcquisitionDocumentDto[] = value.documents.map(
                                temp =>
                                    temp === document ?
                                        ({
                                            ...temp,
                                            ...formData
                                        })
                                        : temp)

                            onChange({
                                ...value,
                                vinCheckDigit: formData.vinCheckDigit,
                                licensePlate: formData.licensePlate,
                                documents: updatedList
                            })
                        }}
                        onBlur={onBlur}
                        header={!disabled && <DeleteVdaDocHeader onDelete={()=>{
                            setDeleteDocumentId(document.documentId)
                            deleteDialog.dialogOpen = true
                        }}/> }
                    />
                case "COR_II":
                    return <VdaDocumentCor2Form
                        label={DocumentTypeLabels.COR_II}
                        disabled={disabled}
                        disableLicensePlate={leadingLicensePlate !== document.type}
                        key={index}
                        validation={{
                            vinCheckDigit: validation.vinCheckDigit,
                            documentId: errorByDocument?.filter(error => error.field?.endsWith('.documentId'))[0]?.message,
                            position: errorByDocument?.filter(error => error.field?.endsWith('.position'))[0]?.message,
                            corNumber: errorByDocument?.filter(error => error.field?.endsWith('.corNumber'))[0]?.message,
                            licensePlate: errorByDocument?.filter(error => error.field?.endsWith('.licensePlate'))[0]?.message,
                            registrationDateFirst: errorByDocument?.filter(error => error.field?.endsWith('.registrationDateFirst'))[0]?.message,
                            registrationDateLatest: errorByDocument?.filter(error => error.field?.endsWith('.registrationDateLatest'))[0]?.message,
                        }}
                        value={{
                            vin: value.vin,
                            vinCheckDigit: value.vinCheckDigit,
                            documentId: document.documentId,
                            position: document.position,
                            corNumber: document.corNumber,
                            licensePlate: value.licensePlate,
                            registrationDateFirst: document.registrationDateFirst,
                            registrationDateLatest: document.registrationDateLatest
                        }}
                        onChange={(formData) => {

                            const updatedList: VehicleDataAcquisitionDocumentDto[] = value.documents.map(
                                temp =>
                                    temp === document ?
                                        ({
                                            ...temp,
                                            ...formData
                                        })
                                        : temp)

                            onChange({
                                ...value,
                                vinCheckDigit: formData.vinCheckDigit,
                                licensePlate: formData.licensePlate,
                                documents: updatedList
                            })
                        }}
                        onBlur={onBlur}
                        header={!disabled && <DeleteVdaDocHeader onDelete={()=>{
                            setDeleteDocumentId(document.documentId)
                            deleteDialog.dialogOpen = true
                        }}/> }
                    />
                case "ABE":
                    return createDefaultDocumentForm(DocumentTypeLabels.ABE, document, index, errorByDocument);
                case "CERT":
                    return createDefaultDocumentForm(DocumentTypeLabels.CERT, document, index, errorByDocument);
                case "CONTRACT":
                    return createDefaultDocumentForm(DocumentTypeLabels.CONTRACT, document, index, errorByDocument,);
                case "SALARY_STATEMENT":
                    return createDefaultDocumentForm(DocumentTypeLabels.SALARY_STATEMENT, document, index, errorByDocument);
                case "ID_CARD":
                    return createDefaultDocumentForm(DocumentTypeLabels.ID_CARD, document, index, errorByDocument);
                case "OTHER":
                    return createDefaultDocumentForm(DocumentTypeLabels.OTHER, document, index, errorByDocument);
                case "CAR_KEY":
                    return <VdaDocumentCarKeyForm
                        label={DocumentTypeLabels.CAR_KEY}
                        disabled={disabled}
                        key={index}
                        validation={{
                            documentId: errorByDocument?.filter(error => error.field?.endsWith('.documentId'))[0]?.message,
                            position: errorByDocument?.filter(error => error.field?.endsWith('.position'))[0]?.message,
                        }}
                        value={{
                            vin: value.vin,
                            documentId: document.documentId,
                            position: document.position,
                            note: document.note ?? value.licensePlate,
                        }}
                        onChange={(formData) => {

                            const updatedList: VehicleDataAcquisitionDocumentDto[] = value.documents.map(
                                temp =>
                                    temp === document ?
                                        ({
                                            ...temp,
                                            ...formData
                                        })
                                        : temp)

                            onChange({
                                ...value,
                                documents: updatedList
                            })
                        }}
                        onBlur={onBlur}
                        header={!disabled && <DeleteVdaDocHeader onDelete={()=>{
                            setDeleteDocumentId(document.documentId)
                            deleteDialog.dialogOpen = true
                        }}/> }
                    />
                case "LICENSE_PLATE_FRONT": {
                    return (
                        <VdaDocumentLicensePlateForm
                            label={DocumentTypeLabels[document.type]}
                            disabled={disabled}
                            disableLicensePlate={leadingLicensePlate !== document.type}
                            key={index}
                            validation={{
                                documentId: errorByDocument?.filter(error => error.field?.endsWith('.documentId'))[0]?.message,
                                position: errorByDocument?.filter(error => error.field?.endsWith('.position'))[0]?.message,
                                licensePlate: errorByDocument?.filter(error => error.field?.endsWith('.licensePlate'))[0]?.message
                            }}
                            value={{
                                vin: value.vin,
                                position: document.position,
                                documentId: document.documentId,
                                licensePlate: value.licensePlate,
                            }}
                            onChange={(formData) => {
                                const updatedList: VehicleDataAcquisitionDocumentDto[] = value.documents.map(
                                    temp =>
                                        temp === document ?
                                            ({
                                                ...temp,
                                                ...formData
                                            })
                                            : temp)

                                onChange({
                                    ...value,
                                    licensePlate: formData.licensePlate,
                                    documents: updatedList
                                })
                            }}
                            onBlur={onBlur}
                            header={!disabled && <DeleteVdaDocHeader onDelete={()=>{
                                setDeleteDocumentId(document.documentId)
                                deleteDialog.dialogOpen = true
                            }}/> }
                        />
                    );
                }

                case "LICENSE_PLATE_REAR": {
                    return (
                        <VdaDocumentLicensePlateForm
                            label={DocumentTypeLabels[document.type]}
                            disabled={disabled}
                            disableLicensePlate={leadingLicensePlate !== document.type}
                            key={index}
                            validation={{
                                documentId: errorByDocument?.filter(error => error.field?.endsWith('.documentId'))[0]?.message,
                                position: errorByDocument?.filter(error => error.field?.endsWith('.position'))[0]?.message,
                                licensePlate: errorByDocument?.filter(error => error.field?.endsWith('.licensePlate'))[0]?.message
                            }}
                            value={{
                                vin: value.vin,
                                position: document.position,
                                documentId: document.documentId,
                                licensePlate: value.licensePlate,
                            }}
                            onChange={(formData) => {
                                const updatedList: VehicleDataAcquisitionDocumentDto[] = value.documents.map(
                                    temp =>
                                        temp === document ?
                                            ({
                                                ...temp,
                                                ...formData
                                            })
                                            : temp)

                                onChange({
                                    ...value,
                                    licensePlate: formData.licensePlate,
                                    documents: updatedList
                                })
                            }}
                            onBlur={onBlur}
                            header={!disabled && <DeleteVdaDocHeader onDelete={()=>{
                                setDeleteDocumentId(document.documentId)
                                deleteDialog.dialogOpen = true
                            }}/> }
                        />
                    );
                }
                case "WARRANTY":
                    return createDefaultDocumentForm(DocumentTypeLabels.WARRANTY, document, index, errorByDocument);
                case "RADIO_CODE":
                    return createDefaultDocumentForm(DocumentTypeLabels.RADIO_CODE, document, index, errorByDocument);
                default:
                    return <></>
            }
        })}
    </div>

}

export {
    VdaForm
}
